/**
 * IndexedDB cache for the inbox.
 *
 * Shape:
 *   conversations — keyPath id, indexed by updatedAt and lastMessageAt.
 *   messages      — keyPath id, indexed by [conversationId, createdAt].
 *   meta          — keyPath key; free-form rows (lastSeenByKey, userId, schemaVersion).
 *   outbox        — keyPath id; queued sends drained by useOutboxDrainer.
 *
 * Read it as React Query `initialData` to paint the inbox instantly on
 * cold start, then let the network query reconcile. Writes happen on
 * every successful network fetch and on socket events.
 */

import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { Conversation, Message } from '@/lib/api/conversations';

const DB_NAME = 'chatbaby';
const DB_VERSION = 1;

const MAX_CONVERSATIONS = 500;
const MAX_MESSAGES_PER_CONV = 200;

interface ChatBabySchema extends DBSchema {
  conversations: {
    key: string;
    value: Conversation;
    indexes: { 'by-updatedAt': string; 'by-lastMessageAt': string };
  };
  messages: {
    key: string;
    value: Message;
    indexes: { 'by-conv-time': [string, string] };
  };
  meta: {
    key: string;
    value: { key: string; value: unknown };
  };
  outbox: {
    key: string;
    // Full type lives in lib/realtime/outbox.ts; store as a generic record
    // so idb's type inference doesn't force a circular import here.
    value: Record<string, unknown> & { id: string; conversationId: string; createdAt: string };
    indexes: { 'by-created': string; 'by-conversation': string };
  };
}

let dbPromise: Promise<IDBPDatabase<ChatBabySchema>> | null = null;

export function getDB(): Promise<IDBPDatabase<ChatBabySchema>> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('IDB is browser-only'));
  }
  if (!dbPromise) {
    dbPromise = openDB<ChatBabySchema>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('conversations')) {
          const s = db.createObjectStore('conversations', { keyPath: 'id' });
          s.createIndex('by-updatedAt', 'updatedAt');
          s.createIndex('by-lastMessageAt', 'lastMessageAt');
        }
        if (!db.objectStoreNames.contains('messages')) {
          const s = db.createObjectStore('messages', { keyPath: 'id' });
          s.createIndex('by-conv-time', ['conversationId', 'createdAt']);
        }
        if (!db.objectStoreNames.contains('meta')) {
          db.createObjectStore('meta', { keyPath: 'key' });
        }
        if (!db.objectStoreNames.contains('outbox')) {
          const s = db.createObjectStore('outbox', { keyPath: 'id' });
          s.createIndex('by-created', 'createdAt');
          s.createIndex('by-conversation', 'conversationId');
        }
      },
    });
  }
  return dbPromise;
}

function idle(cb: () => void): void {
  const ric = (typeof window !== 'undefined' && 'requestIdleCallback' in window)
    ? (window as Window & { requestIdleCallback?: (cb: () => void) => void }).requestIdleCallback
    : undefined;
  if (ric) ric(cb);
  else setTimeout(cb, 0);
}

/** Upsert a list of conversations. Eviction runs async after the write. */
export async function cacheConversations(list: Conversation[]): Promise<void> {
  if (!list.length) return;
  try {
    const db = await getDB();
    const tx = db.transaction('conversations', 'readwrite');
    await Promise.all(list.map((c) => tx.store.put(c)));
    await tx.done;
    idle(() => evictConversations().catch(() => {}));
  } catch {
    /* swallow — cache is best-effort */
  }
}

/** Most-recent-first, capped at `limit`. Returns [] on any error. */
export async function readConversations(limit = 100): Promise<Conversation[]> {
  try {
    const db = await getDB();
    const idx = db.transaction('conversations').store.index('by-lastMessageAt');
    const out: Conversation[] = [];
    let cursor = await idx.openCursor(null, 'prev');
    while (cursor && out.length < limit) {
      out.push(cursor.value);
      cursor = await cursor.continue();
    }
    return out;
  } catch {
    return [];
  }
}

export async function deleteConversation(id: string): Promise<void> {
  try {
    const db = await getDB();
    await db.delete('conversations', id);
  } catch {
    /* ignore */
  }
}

async function evictConversations(): Promise<void> {
  const db = await getDB();
  const count = await db.count('conversations');
  if (count <= MAX_CONVERSATIONS) return;
  const toDelete = count - MAX_CONVERSATIONS + 50; // batch so we don't thrash
  const tx = db.transaction('conversations', 'readwrite');
  const idx = tx.store.index('by-lastMessageAt');
  let cursor = await idx.openCursor();
  let deleted = 0;
  while (cursor && deleted < toDelete) {
    await cursor.delete();
    deleted++;
    cursor = await cursor.continue();
  }
  await tx.done;
}

export async function cacheMessages(conversationId: string, msgs: Message[]): Promise<void> {
  if (!msgs.length) return;
  try {
    const db = await getDB();
    const tx = db.transaction('messages', 'readwrite');
    await Promise.all(
      msgs
        .filter((m) => !m.id.startsWith('temp-'))
        .map((m) => tx.store.put(m))
    );
    await tx.done;
    idle(() => evictMessages(conversationId).catch(() => {}));
  } catch {
    /* ignore */
  }
}

/** Oldest-first, capped at `limit`. Returns [] on any error. */
export async function readMessages(conversationId: string, limit = 50): Promise<Message[]> {
  try {
    const db = await getDB();
    const idx = db.transaction('messages').store.index('by-conv-time');
    const range = IDBKeyRange.bound(
      [conversationId, ''],
      [conversationId, '\uffff'],
    );
    const out: Message[] = [];
    let cursor = await idx.openCursor(range, 'prev'); // newest first
    while (cursor && out.length < limit) {
      out.push(cursor.value);
      cursor = await cursor.continue();
    }
    return out.reverse(); // back to oldest-first, matching API shape
  } catch {
    return [];
  }
}

export async function deleteMessage(id: string): Promise<void> {
  try {
    const db = await getDB();
    await db.delete('messages', id);
  } catch {
    /* ignore */
  }
}

async function evictMessages(conversationId: string): Promise<void> {
  const db = await getDB();
  const tx = db.transaction('messages', 'readwrite');
  const idx = tx.store.index('by-conv-time');
  const range = IDBKeyRange.bound([conversationId, ''], [conversationId, '\uffff']);
  const count = await idx.count(range);
  if (count <= MAX_MESSAGES_PER_CONV) {
    await tx.done;
    return;
  }
  const toDelete = count - MAX_MESSAGES_PER_CONV;
  let cursor = await idx.openCursor(range); // oldest first
  let deleted = 0;
  while (cursor && deleted < toDelete) {
    await cursor.delete();
    deleted++;
    cursor = await cursor.continue();
  }
  await tx.done;
}

export async function getMeta<T = unknown>(key: string): Promise<T | undefined> {
  try {
    const db = await getDB();
    const row = await db.get('meta', key);
    return row?.value as T | undefined;
  } catch {
    return undefined;
  }
}

export async function setMeta<T>(key: string, value: T): Promise<void> {
  try {
    const db = await getDB();
    await db.put('meta', { key, value });
  } catch {
    /* ignore */
  }
}

/**
 * If the stored user id doesn't match, wipe every store except `meta` (so
 * we can still record the new user id). Prevents one user seeing another's
 * cached inbox on a shared machine. Returns true if a purge actually ran.
 */
export async function purgeIfUserChanged(currentUserId: string): Promise<boolean> {
  try {
    const prev = await getMeta<string>('userId');
    if (prev === currentUserId) return false;
    const db = await getDB();
    const tx = db.transaction(['conversations', 'messages', 'outbox'], 'readwrite');
    await Promise.all([
      tx.objectStore('conversations').clear(),
      tx.objectStore('messages').clear(),
      tx.objectStore('outbox').clear(),
    ]);
    await tx.done;
    await setMeta('userId', currentUserId);
    return true;
  } catch {
    return false;
  }
}
